import { AppContextService } from '../../../services/app-context.service';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Campaign, CampaignProgress, CampaignWorkflow, SaveCampaign, campaignServiceLines, campaignServicesLabel, campaignStatusLabel } from '../../../models/campaign.models';
import { CampaignLeadPlanDraft, CampaignTargetsComponent } from './campaign-targets.component';
import { CampaignApiService, campaignErrorMessage } from '../../../services/campaign-api.service';
import { AuthService } from '../../../services/auth.service';
import { EditSessionRegistry } from '../../../shared/edit-session/edit-session-registry.service';
import { parseLocalDateString, todayLocalDateString } from '../../../shared/date.util';
import { ConfirmationDialogService } from '../../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { LeadFeature } from '../../../map/features/leads/lead-feature';
import { MapFeatureManagerService } from '../../../map/core/map-feature-manager.service';
import { MapNavigationService } from '../../../map/core/map-navigation.service';
import { WorkspaceLayoutService } from '../../../services/workspace-layout.service';

/** A campaign is an ongoing workspace: the plan and results first, then the work behind them. */
export type CampaignTab = 'overview' | 'targets' | 'actions' | 'results';

@Component({
  selector: 'app-campaign-detail',
  host: { class: 'workspace-page--full' },
  templateUrl: './campaign-detail.component.html',
  styleUrls: ['./campaign-detail.component.css'],
})
export class CampaignDetailComponent implements OnInit, OnDestroy {
  @ViewChild(CampaignTargetsComponent) targets?: CampaignTargetsComponent;
  @Output() saved = new EventEmitter<Campaign>();
  readonly leadPlanDrafts = new Map<number, CampaignLeadPlanDraft>();
  readonly serviceLines = campaignServiceLines;
  readonly statusLabel = campaignStatusLabel;
  readonly servicesLabel = campaignServicesLabel;
  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(200), Validators.pattern(/\S/)] }),
    serviceLines: new FormControl<string[]>([], { nonNullable: true, validators: [Validators.required] }),
    targetNewCustomers: new FormControl<number | null>(null, [Validators.min(1), Validators.max(2147483647), Validators.pattern(/^\d+$/)]),
    targetRevenueAmount: new FormControl<number | null>(null, [Validators.min(0.01), Validators.max(999999999999.99), Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
    budgetAmount: new FormControl<number | null>(null, [Validators.min(0), Validators.max(999999999999.99), Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
    startsOn: new FormControl('', { nonNullable: true }),
    endsOn: new FormControl('', { nonNullable: true }),
  }, { validators: control => {
    const start = control.get('startsOn')?.value;
    const end = control.get('endsOn')?.value;
    return start && end && end < start ? { dateRange: true } : null;
  } });
  campaign: Campaign | null = null;
  workflow: CampaignWorkflow | null = null;
  workflowLoading = false;
  workflowError = '';
  tab: CampaignTab = 'overview';
  /** Actions has two views of the same Lead plans: preparing them and following them up. */
  actionView: 'plan' | 'follow-up' = 'plan';
  progress: CampaignProgress | null = null;
  progressLoading = false;
  private workflowRequest?: Subscription;
  private progressRequest?: Subscription;
  private viewContext?: Subscription;
  readonly tabs: readonly { id: CampaignTab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'targets', label: 'Targets' },
    { id: 'actions', label: 'Actions' }, { id: 'results', label: 'Results' },
  ];
  /** The Lead-target views keep their step numbers: 1 plan, 2 targets, 3 plan actions, 4 follow up, 5 results. */
  get viewedStep(): string {
    return this.tab === 'overview' ? '1' : this.tab === 'targets' ? '2'
      : this.tab === 'actions' ? (this.actionView === 'plan' ? '3' : '4') : '5';
  }
  get tabIndex(): number { return this.tabs.findIndex(tab => tab.id === this.tab); }
  /** In-flight writes keep the current view; a new campaign only has its plan. */
  get tabsLocked(): boolean { return this.saving || !!this.targets?.busy; }
  get period(): { start: Date; end: Date; percent: number; label: string } | null {
    const { startsOn, endsOn } = this.form.getRawValue();
    const start = parseLocalDateString(startsOn), end = parseLocalDateString(endsOn);
    if (!start || !end || endsOn < startsOn) return null;
    const day = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
    const today = parseLocalDateString(todayLocalDateString())!;
    const total = day(end) - day(start) + 1;
    const current = day(today) - day(start) + 1;
    return { start, end, percent: Math.max(0, Math.min(100, 100 * current / total)),
      label: current <= 0 ? `Starts in ${1 - current} days` : current > total ? 'Campaign period ended' : `Day ${current} of ${total}` };
  }
  goalPercent(actual: number | undefined, goal: number | null): number {
    return goal ? Math.min(100, Math.round(100 * (actual ?? 0) / goal)) : 0;
  }
  get viewingActionPlans(): boolean { return this.tab === 'actions'; }
  get terminal(): boolean { return !!this.campaign && ['Completed', 'Cancelled'].includes(this.campaign.status); }
  get canManageTargets(): boolean { return !!this.campaign && !this.terminal && this.auth.hasPermission('Lead.Manage'); }

  showTab(tab: CampaignTab): void {
    if (tab === this.tab || this.tabsLocked || (!this.campaign && tab !== 'overview')) return;
    this.tab = tab;
    void this.router.navigate([], { relativeTo: this.route, queryParams: { tab, step: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  selectTabIndex(index: number): void { const tab = this.tabs[index]; if (tab) this.showTab(tab.id); }
  setActionView(view: 'plan' | 'follow-up'): void { if (!this.tabsLocked) this.actionView = view; }
  /** Viewing campaign geography never changes the shared map selection used by Add Leads. */
  showOnMap(): void {
    if (!this.campaign) return;
    const campaignId = this.campaign.id;
    this.layout.openAs('docked-right');
    if (!this.leadLayer.enabled) this.features.toggleFeature(this.leadLayer.key);
    this.api.memberLocations(campaignId).pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
      next: rows => {
        const points = rows.flatMap(row => row.lat != null && row.lng != null ? [{ id: row.leadId, lat: row.lat, lng: row.lng }] : []);
        if (points.length) this.navigation.fitBounds(points.map(point => [point.lat, point.lng] as [number, number]), { maxZoom: 15 });
        this.notice = points.length ? ''
          : 'No campaign Leads have a map location yet.';
      },
      error: error => { this.error = campaignErrorMessage(error, 'Could not load campaign Leads for the map.'); },
    });
  }
  refreshWorkflow(): void {
    if (!this.campaign) return;
    this.workflowRequest?.unsubscribe();
    this.workflowLoading = true; this.workflowError = '';
    this.workflowRequest = this.api.workflow(this.campaign.id)
      .pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
        next: workflow => { this.workflow = workflow; this.workflowLoading = false; },
        error: error => { this.workflow = null; this.workflowLoading = false;
          this.workflowError = campaignErrorMessage(error, 'Could not check campaign progress. Retry to load current data.'); },
      });
    this.progressRequest?.unsubscribe();
    this.progressLoading = true;
    this.progressRequest = this.api.progress(this.campaign.id)
      .pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
        next: progress => { this.progress = progress; this.progressLoading = false; },
        error: () => { this.progress = null; this.progressLoading = false; },
      });
  }
  closeCampaign(): void {
    const to = this.workflow?.closeStatus;
    if (!to || !this.campaign || this.dirty || this.workflowLoading || this.terminal || !this.auth.hasPermission('Lead.Manage')) return;
    const campaignId = this.campaign.id;
    this.confirmation.confirm({ title: 'Close campaign?',
      message: `This campaign will be marked ${to.toLowerCase()}. ${this.workflow!.targetCount} Lead targets and their independent follow-ups will be preserved. Unfinished steps remain visible.`,
      confirmLabel: 'Close campaign', cancelLabel: 'Keep open', danger: true })
      .pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe(confirmed => {
        if (!confirmed || this.saving || this.campaign?.id !== campaignId || this.terminal) return;
        this.saving = true; this.error = '';
        this.api.transition(campaignId, to).pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
          next: campaign => { this.campaign = campaign; this.saving = this.editing = false; this.resetForm();
            this.refreshWorkflow(); this.saved.emit(campaign); this.notice = 'Campaign closed. Lead follow-ups are preserved.'; },
          error: error => { this.saving = false; this.error = campaignErrorMessage(error, 'Could not close this campaign. Refresh and try again.'); this.refreshWorkflow(); },
        });
      });
  }
  isNew = false;
  loading = false;
  saving = false;
  editing = false;
  error = '';
  notice = '';
  private readonly destroyed$ = new Subject<void>();
  private readonly cancelRequests$ = new Subject<void>();
  private readonly unregister: () => void;

  constructor(private readonly route: ActivatedRoute, private readonly router: Router,
    private readonly api: CampaignApiService, public readonly auth: AuthService,
    registry: EditSessionRegistry, private readonly confirmation: ConfirmationDialogService,
    private readonly leadLayer: LeadFeature, private readonly features: MapFeatureManagerService,
    private readonly navigation: MapNavigationService, private readonly layout: WorkspaceLayoutService, private readonly context: AppContextService) {
    this.unregister = registry.register(this);
  }
  get dirty(): boolean { return this.saving || !!this.targets?.dirty || this.leadPlanDrafts.size > 0 || (this.editing && this.form.dirty); }
  get canEdit(): boolean {
    return this.auth.hasPermission('Lead.Manage') && !!this.campaign &&
      ['Draft', 'AwaitingData', 'Ready', 'BlockedData', 'Paused'].includes(this.campaign.status);
  }
  ngOnInit(): void {
    this.viewContext = this.context.registerView({
      key: 'campaign-view', name: 'Campaign view', priority: -20,
      read: () => ({ id: Number(this.route.snapshot.paramMap.get('id')), tab: this.tab, actionView: this.actionView }),
      describe: state => JSON.stringify(state),
      restore: state => {
        if (state.id !== Number(this.route.snapshot.paramMap.get('id'))) return;
        if (this.tabs.some(tab => tab.id === state.tab)) this.tab = state.tab;
        if (state.actionView === 'plan' || state.actionView === 'follow-up') this.actionView = state.actionView;
      },
    });
    this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe(() => this.load());
    this.auth.tenant.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.cancelRequests$.next(); this.leadPlanDrafts.clear();
      this.campaign = null; this.workflow = null; this.workflowLoading = false; this.workflowError = '';
      this.progress = null; this.progressLoading = false;
      this.form.reset();
      this.form.disable();
      this.editing = this.loading = this.saving = false;
      this.error = 'Account changed. Reopen the campaign to continue.';
    });
  }
  load(): void {
    this.cancelRequests$.next(); this.leadPlanDrafts.clear();
    this.campaign = null; this.workflow = null; this.workflowError = ''; this.workflowLoading = false;
    this.progress = null; this.progressLoading = false;
    const requested = this.route.snapshot.queryParamMap?.get('tab');
    this.tab = this.tabs.some(tab => tab.id === requested) ? requested as CampaignTab : 'overview';
    this.actionView = 'plan';
    this.error = this.notice = '';
    this.isNew = this.route.snapshot.data['campaignNew'] === true;
    this.loading = false;
    this.saving = false;
    if (this.isNew) {
      this.tab = 'overview';
      this.form.reset();
      this.editing = this.auth.hasPermission('Lead.Manage');
      if (this.editing) this.form.enable(); else this.form.disable();
      return;
    }
    this.editing = false;
    this.form.disable();
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isSafeInteger(id) || id <= 0) { this.error = 'Invalid campaign.'; return; }
    this.loading = true;
    this.api.get(id).pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
      next: campaign => { this.campaign = campaign; this.resetForm(); this.loading = false; this.refreshWorkflow(); },
      error: error => { this.loading = false; this.error = campaignErrorMessage(error, 'Could not load the campaign. Try again.'); },
    });
  }
  beginEdit(): void {
    if (!this.canEdit) return;
    this.editing = true;
    this.notice = this.error = '';
    this.form.enable();
  }
  setService(value: string, checked: boolean): void {
    if (!this.editing || this.saving) return;
    const control = this.form.controls.serviceLines;
    control.setValue(checked ? [...new Set([...control.value, value])] : control.value.filter(line => line !== value));
    control.markAsDirty();
  }
  save(): void {
    if (!this.editing || this.saving || !this.auth.hasPermission('Lead.Manage')) return;
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    const value = this.form.getRawValue();
    const request: SaveCampaign = {
      name: value.name.trim(), serviceLines: value.serviceLines,
      planning: { description: this.campaign?.planning?.description ?? null,
        serviceOffer: this.campaign?.planning?.serviceOffer ?? null,
        targetNewCustomers: value.targetNewCustomers,
        targetRevenueAmount: value.targetRevenueAmount,
        budgetAmount: value.budgetAmount,
        startsOn: value.startsOn || null, endsOn: value.endsOn || null },
    };
    this.saving = true;
    this.form.disable();
    this.error = '';
    const operation = this.isNew ? this.api.create(request) : this.api.update(this.campaign!.id, request);
    operation.pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
      next: campaign => {
        const created = this.isNew;
        this.isNew = false;
        this.campaign = campaign;
        this.saving = this.editing = false;
        this.resetForm();
        this.notice = 'Campaign saved.';
        this.saved.emit(campaign);
        this.refreshWorkflow();
        if (created) void this.router.navigate(['/campaigns', campaign.id], { replaceUrl: true, queryParams: { tab: 'targets' } });
      },
      error: error => {
        this.saving = false;
        this.form.enable();
        this.error = campaignErrorMessage(error, 'Could not save the campaign. Your changes are still here.');
      },
    });
  }
  cancel(): void {
    if (this.saving) return;
    if (!this.form.dirty) { this.discard(); return; }
    this.confirmation.confirm({ title: 'Discard changes?', message: 'Your unsaved campaign changes will be lost.',
      confirmLabel: 'Discard changes', cancelLabel: 'Keep editing', danger: true })
      .pipe(takeUntil(this.destroyed$)).subscribe(confirmed => { if (confirmed) this.discard(); });
  }
  private discard(): void {
    this.form.markAsPristine();
    this.error = '';
    this.editing = false;
    if (this.isNew) void this.router.navigate(['/campaigns']);
    else this.resetForm();
  }
  private resetForm(): void {
    const campaign = this.campaign;
    this.form.reset({ name: campaign?.name ?? '', serviceLines: campaign?.serviceLines ?? [],
      targetNewCustomers: campaign?.planning?.targetNewCustomers ?? null,
      targetRevenueAmount: campaign?.planning?.targetRevenueAmount ?? null,
      budgetAmount: campaign?.planning?.budgetAmount ?? null,
      startsOn: campaign?.planning?.startsOn ?? '', endsOn: campaign?.planning?.endsOn ?? '' });
    this.form.disable();
  }
  ngOnDestroy(): void {
    this.viewContext?.unsubscribe();
    this.unregister();
    this.destroyed$.next(); this.destroyed$.complete();
    this.cancelRequests$.next(); this.leadPlanDrafts.clear(); this.cancelRequests$.complete();
  }
}
